import { apiService } from "@/lib/api";

const BASE_URL = "/api/v2/candidate-status";

// Sort fields the backend accepts — snake_case, mirroring the DB columns.
// Anything else silently falls back to `sequence`. Exported so the admin
// table and any other caller whitelist against one list instead of two
// drifting copies.
export const CANDIDATE_STATUS_SORT_FIELDS = [
  "sequence",
  "code",
  "name",
  "created_at",
  "is_initial",
  "is_hired",
  "is_rejected",
];

export const CandidateStatusService = {
  async list(params = {}) {
    const response = await apiService.getSecure(`${BASE_URL}/list`, {
      params: {
        flow_id: params.flowId,
        page: params.page ?? 1,
        per_page: params.per_page ?? 10,
        sort_by: CANDIDATE_STATUS_SORT_FIELDS.includes(params.sort_by)
          ? params.sort_by
          : "sequence",
        sort_order: params.sort_order === "desc" ? "desc" : "asc",
      },
    });
    return response.data;
  },

  // `flowId` on get/update/delete is optional, verified server-side, never
  // written — a caller that already knows which flow a status should
  // belong to (every caller in this codebase does) passes it so a stale
  // selection fails loudly (STATUS_FLOW_MISMATCH) instead of silently
  // acting on the wrong flow's status. Always a query param, never the body
  // — it's identifying context for the request, not data being written.
  async getById(statusId, flowId) {
    const response = await apiService.getSecure(`${BASE_URL}/get/${statusId}`, {
      params: flowId ? { flow_id: flowId } : {},
    });
    return response.data;
  },

  async create(payload) {
    const response = await apiService.postSecure(`${BASE_URL}/create`, payload);
    return response.data;
  },

  async update(statusId, payload, flowId) {
    const response = await apiService.patch(`${BASE_URL}/update/${statusId}`, payload, {
      params: flowId ? { flow_id: flowId } : {},
    });
    return response.data;
  },

  async delete(statusId, replacementStatusId = null, flowId) {
    const response = await apiService.delete(`${BASE_URL}/delete/${statusId}`, {
      params: flowId ? { flow_id: flowId } : {},
      data: {
        ...(replacementStatusId ? { replacementStatusId } : {}),
      },
    });
    return response.data;
  },

  // ── Flows — named, reusable candidate-status pipelines within a tenant ────

  async listFlows(params = {}) {
    const response = await apiService.getSecure(`${BASE_URL}/flows/list`, {
      params: {
        page: params.page ?? 1,
        per_page: params.per_page ?? 100,
        sort_by: ["created_at", "name", "is_default", "is_active"].includes(
          params.sort_by
        )
          ? params.sort_by
          : "created_at",
        sort_order: params.sort_order === "desc" ? "desc" : "asc",
      },
    });
    return response.data;
  },

  async getFlow(flowId) {
    const response = await apiService.getSecure(`${BASE_URL}/flows/get/${flowId}`);
    return response.data;
  },

  async createFlow(payload) {
    const response = await apiService.postSecure(`${BASE_URL}/flows/create`, payload);
    return response.data;
  },

  async updateFlow(flowId, payload) {
    const response = await apiService.patch(`${BASE_URL}/flows/update/${flowId}`, payload);
    return response.data;
  },

  async deleteFlow(flowId) {
    const response = await apiService.delete(`${BASE_URL}/flows/delete/${flowId}`);
    return response.data;
  },
};

export default CandidateStatusService;
